import { useCallback, useEffect } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import _ from "lodash"
import { useTheme, Box } from "@mui/material"

import { Q_VENUE } from "constant"

import { pluralize } from "utils/string"
import { convertMapViewObjectToPath } from "utils/map"

import { useMap } from "hooks/map/useMap"
import { useGetUserLocationView, useGetMapViewFromUrl } from "hooks/map"
import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"

import { PageCard } from "components/card"
import { MyLocationButton } from "components/Button"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { SidebarMenu } from "extensions/iconsiam/components/navigation/kiosk-h/SidebarMenu"
import { KioskOrdinalSelector } from "extensions/iconsiam/components/ordinal-selector"
import AttractionButton from "extensions/iconsiam/components/domains/attractions/AttractionButton"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { FeatureToggleChipTabBar } from "components/domains/features"
import { TouristRegistrationPanel } from "extensions/iconsiam/components/domains/TouristRegistrationPanel"
import { SideBannerPanel } from "extensions/iconsiam/components/domains/SideBannerPanel"

export const MapOrdinalKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
}) => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { userLocation } = useGeoLocation()
  const { ordinal } = useParams()
  const { isMultiOrdinalVenue } = useVenue()
  const {
    viewingVenueId,
    viewingVenueView,
    quickSearchCategories,
    locationOrdinal,
    locationVenueId,
    sortedLevels,
  } = useMap()

  const { dataLoaded } = useVenue()
  const { mapLoaded, changeLevelByOrdinal, flyTo, resetMapState } =
    useIndoorMap()

  const parsedOrdinal = Number(ordinal)
  const hasUserLocationAtCurrentFloor = locationOrdinal === parsedOrdinal

  const userLocationView = useGetUserLocationView()
  const { mapView } = useGetMapViewFromUrl()

  const targetMapView =
    mapView || userLocationView || _.omit(viewingVenueView, ["id"])

  // If targetMapView exists, flyTo target MapView
  useEffect(() => {
    flyTo(targetMapView)
  }, [flyTo, targetMapView])

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components[`AppMap`] || {}

  const {
    toolbarContainer,
    locationButtonContainer,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    ordinalSelector,
    attractionButtonContainer,
    touristRegistrationPanel,
    touristRegistrationPanelWrapper,
    sideBannerPanel,
    sideBannerPanelWrapper,
  } = styleOverrides

  // ─── Hooks ───

  /**
   * Resets the map view to default state.
   * Triggered when both data is loaded and the map has finished loading.
   */
  useEffect(() => {
    if (dataLoaded && mapLoaded) {
      resetMapState()
    }
  }, [resetMapState, dataLoaded, mapLoaded])

  /**
   * Updates map level based on the current ordinal from route params /maps/ordinal/{ordinal}.
   * Triggered when ordinal value changes.
   */
  useEffect(() => {
    if (dataLoaded && mapLoaded) changeLevelByOrdinal(parsedOrdinal)
  }, [changeLevelByOrdinal, parsedOrdinal, dataLoaded, mapLoaded])

  // ─── Handler ───
  const handleOrdinalChange = useCallback(
    ({ ordinal, venueId }) => {
      if (venueId) searchParams.set(Q_VENUE, venueId)

      const ordinalPath = ordinal !== null ? `ordinal/${ordinal}` : ""
      const search = searchParams.size > 0 ? `?${searchParams.toString()}` : ""

      navigate(`/maps/${ordinalPath}${search}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const handleClickQuickSearch = useCallback(
    (type, category) => {
      //This condition prevent navigation when ordinal or viewingVenueId is undefined
      if (_.isNil(ordinal) || _.isNil(viewingVenueId)) return
      const pluralType = pluralize(type)
      navigate(
        `/maps/${pluralType}/${category}?from-ordinal=${ordinal}&venue=${viewingVenueId}`
      )
    },
    [navigate, ordinal, viewingVenueId]
  )

  const handleClickMyLocation = useCallback(() => {
    //This condition prevent navigation when ordinal is undefined
    if (_.isNil(ordinal) || !userLocation) return
    const userOrdinal = _.get(userLocation, "properties.ordinal", 0)
    const mapViewPath = convertMapViewObjectToPath(userLocationView)
    navigate(`/maps/ordinal/${userOrdinal}?mv=${mapViewPath}`, {
      replace: true,
    })
  }, [navigate, ordinal, userLocationView, userLocation])

  useEffect(() => {
    if (hasUserLocationAtCurrentFloor) handleClickMyLocation()
  }, [hasUserLocationAtCurrentFloor, handleClickMyLocation])

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  return (
    //@ts-ignore
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <Box sx={toolbarContainer}>
        <Box sx={quickLinkContainer}>
          <FeatureToggleChipTabBar
            categories={quickSearchCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
      </Box>
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelector}>
          <KioskOrdinalSelector
            selectedOrdinal={parsedOrdinal}
            levels={sortedLevels}
            onChangeOrdinal={handleOrdinalChange}
            currentVenue={locationVenueId}
            viewingVenueId={viewingVenueId}
            locationOrdinal={locationOrdinal}
          />
        </Box>
      )}

      <Box sx={touristRegistrationPanelWrapper}>
        <TouristRegistrationPanel sx={touristRegistrationPanel} />
      </Box>

      <Box sx={sideBannerPanelWrapper}>
        <SideBannerPanel sx={sideBannerPanel} />
      </Box>

      <Box sx={locationButtonContainer}>
        {userLocation && <MyLocationButton onClick={handleClickMyLocation} />}
      </Box>

      <Box sx={attractionButtonContainer}>
        <AttractionButton />
      </Box>
    </Layout>
  )
}
